import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import SpecialNotice from '../components/SpecialNotice';
import HeroSection from '../components/sections/HeroSection';
import AboutPortal from '../components/sections/AboutPortal';
import WhoCanAccess from '../components/sections/WhoCanAccess';
import AboutCompany from '../components/sections/AboutCompany';
import PortfolioHome from '../components/sections/PortfolioHome';
import MemberBenefits from '../components/sections/MemberBenefits';
import GettingStarted from '../components/sections/GettingStarted';
import SecurityPrivacy from '../components/sections/SecurityPrivacy';
import ComplianceDisclaimer from '../components/sections/ComplianceDisclaimer';
import ContactSupport from '../components/sections/ContactSupport';
import MemberLoginCTA from '../components/sections/MemberLoginCTA';
import BlogsSection from '../components/sections/BlogsSection';
import CTASection from '../components/sections/CTASection';
import Footer from '../components/Footer';
import { X, Download, FileText, Image as ImageIcon, Calendar } from 'lucide-react';

// Add CSS animation
const modalStyles = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  .animate-fadeIn {
    animation: fadeIn 0.3s ease-out;
  }
`;

const HomePage = () => {
    const [contentData, setContentData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // PDF Popup State
    const [pdfData, setPdfData] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(true);
    const [showPdfPopup, setShowPdfPopup] = useState(false);

    // Fetch content data from API
    useEffect(() => {
        const fetchContentData = async () => {
            try {
                setLoading(true);
                const response = await axios.get('https://api.marketmaestroovip.in/api/content');
                
                if (response.data.success) {
                    setContentData(response.data.data || {});
                } else {
                    setError('Failed to fetch content');
                }
            } catch (err) {
                console.error('Error fetching content:', err);
                setError('Error loading content');
            } finally {
                setLoading(false);
            }
        };

        fetchContentData();
    }, []);

    // Fetch PDF data
    useEffect(() => {
        const fetchPDF = async () => {
            try {
                setPdfLoading(true);
                console.log('Fetching PDF...');
                const response = await axios.get('https://api.marketmaestroovip.in/api/pdf');
                
                console.log('PDF Response:', response.data);
                
                if (response.data) {
                    setPdfData(response.data);
                    setShowPdfPopup(true);
                } else {
                    console.log('No PDF data received');
                    setPdfData(null);
                }
            } catch (err) {
                console.error('Error fetching PDF:', err);
                setPdfData(null);
            } finally {
                setPdfLoading(false);
            }
        };

        fetchPDF();
    }, []);

    const closePdfPopup = () => {
        console.log('Closing popup');
        setShowPdfPopup(false);
    };

    const handleDownload = () => {
        if (pdfData) {
            window.open(`https://api.marketmaestroovip.in/${pdfData.pdf}`, '_blank');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).replace(/\//g, '/');
    };

    // If loading, show loading indicator
    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading content...</p>
                </div>
            </div>
        );
    }

    // If error, show error message
    if (error) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center p-4 bg-red-50 rounded-lg">
                    <p className="text-red-600">Error: {error}</p>
                    <button 
                        onClick={() => window.location.reload()} 
                        className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const pdfUrl = pdfData ? `https://api.marketmaestroovip.in/${pdfData.pdf}` : '';
    const posterUrl = pdfData?.poster ? `https://api.marketmaestroovip.in/${pdfData.poster}` : null;

    return (
        <div className="min-h-screen bg-white relative">
            {/* Add styles */}
            <style>{modalStyles}</style>
            
            {/* PDF Popup Modal */}
            {showPdfPopup && pdfData && (
                <>
                    {/* Transparent overlay */}
                    <div 
                        className="fixed inset-0 z-[99998] hidden"
                        style={{ 
                            backgroundColor: 'rgba(0, 0, 0, 0.15)',
                            backdropFilter: 'blur(1px)'
                        }}
                        onClick={closePdfPopup}
                    />
                    
                    {/* Popup content */}
                    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-6 md:p-8 pointer-events-none hidden">
                        <div 
                            className="bg-white rounded-lg w-full max-w-[95%] sm:max-w-[90%] md:max-w-4xl lg:max-w-5xl xl:max-w-6xl max-h-[90vh] flex flex-col animate-fadeIn shadow-2xl pointer-events-auto overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="flex justify-between items-center p-4 sm:p-5 border-b border-gray-200">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                        <FileText className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                                            Important Document
                                        </h3>
                                        <p className="text-xs sm:text-sm text-gray-500">
                                            View and download document
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={closePdfPopup}
                                    className="p-2 hover:bg-gray-100 rounded-full transition"
                                >
                                    <X className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>

                            {/* Two Column Content */}
                            <div className="flex-1 overflow-y-auto p-4 sm:p-5">
                                <div className="flex flex-col md:flex-row gap-4 sm:gap-5">
                                    {/* Left Column - Poster */}
                                    <div className="md:w-1/2">
                                        <div className="bg-gray-50 rounded-lg overflow-hidden h-full flex items-center justify-center p-2">
                                            {posterUrl ? (
                                                <img
                                                    src={posterUrl}
                                                    alt="Document Poster"
                                                    className="w-full h-auto object-contain max-h-[50vh] md:max-h-[50vh] rounded"
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = 'https://via.placeholder.com/600x800?text=Poster+Not+Available';
                                                    }}
                                                />
                                            ) : (
                                                <div className="text-center p-8">
                                                    <ImageIcon className="w-20 h-20 mx-auto text-gray-400 mb-3" />
                                                    <p className="text-gray-500">No poster available</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Right Column - Document Info */}
                                    <div className="md:w-1/2 flex flex-col">
                                        <div className="bg-gray-50 rounded-lg p-5 sm:p-6 mb-4">
                                            <h4 className="text-lg font-semibold text-gray-900 mb-4">Document Information</h4>
                                            
                                            {/* Document Name */}
                                            <div className="mb-4">
                                                <p className="text-sm text-gray-500 mb-1">Document Name</p>
                                                <div className="flex items-center gap-2 bg-white p-3 rounded border border-gray-200">
                                                    <FileText className="w-5 h-5 text-red-500 flex-shrink-0" />
                                                    <span className="font-medium text-gray-900 truncate">document.pdf</span>
                                                </div>
                                            </div>

                                            {/* Upload Date */}
                                            <div className="mb-4">
                                                <p className="text-sm text-gray-500 mb-1">Uploaded Date</p>
                                                <div className="flex items-center gap-2 bg-white p-3 rounded border border-gray-200">
                                                    <Calendar className="w-5 h-5 text-blue-500 flex-shrink-0" />
                                                    <span className="text-gray-900">
                                                        {formatDate(pdfData.created_at)}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Download Button */}
                                            <button
                                                onClick={handleDownload}
                                                className="w-full mt-4 px-5 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center gap-2 transition font-medium text-base"
                                            >
                                                <Download className="w-5 h-5" />
                                                Download PDF
                                            </button>

                                            {/* Additional Info */}
                                            {pdfData.updated_at && pdfData.updated_at !== pdfData.created_at && (
                                                <p className="text-xs text-gray-400 text-center mt-3">
                                                    Last updated: {formatDate(pdfData.updated_at)}
                                                </p>
                                            )}
                                        </div>

                                   
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="p-4 sm:p-5 border-t border-gray-200 flex justify-end">
                                <button
                                    onClick={closePdfPopup}
                                    className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Header */}
            <Header />

            {/* Main Content Sections */}
            <main>
                <HeroSection contentData={contentData} />
                <PortfolioHome contentData={contentData} />
                <AboutPortal contentData={contentData} />
                <WhoCanAccess contentData={contentData} />
                <AboutCompany contentData={contentData} />
                <MemberBenefits contentData={contentData} />
                <GettingStarted contentData={contentData} />
                <SecurityPrivacy contentData={contentData} />
                <ComplianceDisclaimer contentData={contentData} />
                <ContactSupport contentData={contentData} />
                <MemberLoginCTA contentData={contentData} /> 
                <BlogsSection contentData={contentData} />
                <CTASection contentData={contentData} />
            </main>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default HomePage;